import { type ReactElement, useEffect, useState } from 'react';
import { s } from '@/theme/tokens';
import { DeltaWithIndicator } from '@/ui/composites/deltaIndicator';
import { getDeltaTextColor } from '@/ui/composites/table/deltaColors';
import { Stack } from '@/ui/layout/Stack';
import { Box, Chip, Skeleton, Surface, Text } from '@/ui/primitives';
import type { MetricValueItem } from './metricValueItems';
import type { Theme } from '@mui/material/styles';

const metricCardHeightPx = 40;

type MetricValueCardsRowProps = {
  items: MetricValueItem[];
  loading?: boolean;
  skeletonCount?: number;
  centerWrapped?: boolean;
};

type MetricValueCardsRowSkeletonProps = {
  count?: number;
};

const MetricValueCardsRowSkeleton = ({
  count = 3,
}: MetricValueCardsRowSkeletonProps): ReactElement => (
  <Stack direction="row" wrap="wrap" gap="md" align="stretch">
    {Array.from({ length: count }).map((_, index) => (
      <Surface
        key={index}
        padding="none"
        sx={{
          backgroundColor: 'background.paper',
          width: { xs: '100%', sm: s('4xl') },
          flex: { xs: '0 0 100%', sm: `0 0 ${s('4xl')}` },
          minHeight: metricCardHeightPx,
          height: metricCardHeightPx,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ width: '100%', height: metricCardHeightPx }}>
          <Skeleton variant="rounded" width="100%" height={metricCardHeightPx} />
        </Box>
      </Surface>
    ))}
  </Stack>
);

const isPlaceholderItem = (item: MetricValueItem): boolean => item.valueLabel === '-';

const resolveDeltaColor = (muiTheme: Theme, tone: MetricValueItem['deltaTone']): string =>
  tone === 'positive'
    ? getDeltaTextColor(muiTheme, true)
    : tone === 'negative'
      ? getDeltaTextColor(muiTheme, false)
      : muiTheme.palette.text.secondary;

const renderDeltaValue = (label: string, tone: MetricValueItem['deltaTone']): ReactElement => {
  if (tone === 'positive' || tone === 'negative') {
    return <DeltaWithIndicator label={label} isPositive={tone === 'positive'} />;
  }
  return <>{label}</>;
};

export const MetricValueCardsRow = ({
  items,
  loading,
  skeletonCount,
  centerWrapped = false,
}: MetricValueCardsRowProps): ReactElement => {
  const inferredPlaceholderItems = items.length > 0 && items.every(isPlaceholderItem);
  const shouldShowSkeleton = Boolean(loading) && items.length === 0;
  const showPlaceholderValues = inferredPlaceholderItems || (Boolean(loading) && items.length > 0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!shouldShowSkeleton && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [hasAnimated, shouldShowSkeleton]);

  if (shouldShowSkeleton) {
    const resolvedCount = skeletonCount ?? 3;
    return <MetricValueCardsRowSkeleton count={resolvedCount} />;
  }

  return (
    <Box className={hasAnimated ? undefined : 'flow-card-enter'}>
      <Stack
        direction="row"
        wrap="wrap"
        gap="md"
        align="stretch"
        justify={{ xs: 'center', sm: centerWrapped ? 'center' : 'flex-start' }}
      >
        {items.map((item) => (
          <Surface
            key={item.id}
            padding="none"
            sx={{
              backgroundColor: 'background.paper',
              px: s('lg'),
              py: s('md'),
              width: { xs: '100%', sm: 'auto' },
              flex: { xs: '0 0 100%', sm: '0 0 auto' },
              minHeight: metricCardHeightPx,
              height: metricCardHeightPx,
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-start' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {(() => {
              const showDelta = item.deltaLabel.length > 0;
              const gridColumns = showDelta ? '1fr 1fr 1fr' : '1fr 1fr';
              return (
                <Box
                  sx={{
                    display: { xs: 'grid', sm: 'flex' },
                    gridTemplateColumns: { xs: gridColumns, sm: 'none' },
                    alignItems: 'center',
                    columnGap: { xs: s('sm'), sm: s('md') },
                    rowGap: { xs: s('xs'), sm: s('none') },
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Text
                      role="overline"
                      color="textSecondary"
                      sx={{
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        paddingTop: '2px',
                      }}
                    >
                      {item.label}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: {
                        xs: showDelta ? 'center' : 'flex-end',
                        sm: 'flex-start',
                      },
                    }}
                  >
                    {item.id === 'passRate' && item.valueLabel === '-' && showPlaceholderValues ? (
                      <Text role="sectionTitle">-</Text>
                    ) : (
                      <Chip label={item.valueLabel} tone={item.valueTone} size="small" />
                    )}
                  </Box>
                  {showDelta ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}
                    >
                      <Text
                        role="bodyMuted"
                        component="span"
                        sx={(muiTheme) => ({
                          color: resolveDeltaColor(muiTheme, item.deltaTone),
                          fontSize: muiTheme.typography.body.fontSize,
                          whiteSpace: 'nowrap',
                        })}
                      >
                        {renderDeltaValue(item.deltaLabel, item.deltaTone)}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              );
            })()}
          </Surface>
        ))}
      </Stack>
    </Box>
  );
};
